/**
 * Create Consent Drawer
 *
 * Drawer component that mirrors the Angular create consent form.
 * Handles aggregator selection, multi-consent templates, customer details,
 * and optional additional information sections.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
} from 'equal-ds-ui';
import { StyledQRCode } from '../../ui/StyledQRCode';
import { Copy, Check, MessageSquare, Plus } from 'lucide-react';
import { FormField } from '../../ui/FormField';
import { Toast } from '../../ui/Toast';
import { consentRequestAPI } from '../../../services/consent-request-api.service';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { getAssistedJourneyFlags, toWhatsAppNumber } from '../../../utils/assisted-journey';
import type { AssistedJourneyFlags } from '../../../utils/assisted-journey';
import type {
  ConsentCreatePayload,
  ConsentUserProfile,
  StateRecord,
} from '../../../types/consent';
import type { ProductRecord } from '../../../services/products-api.service';
import './finpro-logs-drawer-styles.css';

/** Border color for dropdown triggers - matches Tailwind gray-500 (#6b7280) */
const DROPDOWN_BORDER_COLOR = '#6b7280';

export interface AggregatorOption {
  value: string;
  label: string;
  logoUrl?: string;
}

/** Success data for the consent journey created view */
export interface ConsentSuccessData {
  consentLink: string;
  consentHandle: string;
  mobileNumber: string;
}

export interface CreateConsentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ConsentCreatePayload) => Promise<void>;
  isSubmitting: boolean;
  aggregators: AggregatorOption[];
  products: ProductRecord[];
  productDivisions: string[];
  states: StateRecord[];
  multiConsentLimit: number;
  allowMultiConsent: boolean;
  allowAdditionalInfo: boolean;
  allowStateSelection: boolean;
  endUserListEnabled: boolean;
  defaultAggregator?: string;
  onFetchEndUserDetails?: (mobileNumber: string) => Promise<ConsentUserProfile | null>;
  onGenerateAccountId?: () => string;
  onDownloadGuidelines?: () => void;
  onViewTerms?: () => void;
  successMessage?: string | null;
  errorMessage?: string | null;
  /** When set, shows the success view with QR code and sharing options */
  consentSuccessData?: ConsentSuccessData | null;
  /** Callback to reset the success state and show form again */
  onResetSuccess?: () => void;
  /** Product-level assisted journey flags computed from selected products */
  assistedJourneyFlags?: AssistedJourneyFlags | null;
}

interface CreateConsentFormValues {
  accountAggregator: string;
  productIDList: string[];
  ProductDivision: string;
  accountID: string;
  partyIdentifierType: string;
  partyIdentifierValue: string;
  pan: string;
  dob: string;
  vua: string;
  email: string;
  firstName: string;
  lastName: string;
  pincode: string;
  mobileNumber: string;
  expiresOn: string;
  genderCode: string;
  addressline: string;
  request_type: string;
  user_state: string;
}

type FormErrors = Partial<Record<keyof CreateConsentFormValues, string>>;

const PARTY_IDENTIFIER_OPTIONS = [
  { value: 'MOBILE', label: 'Mobile Number' },
];

/**
 * PAN format: AAAAA9999A. Keeps only characters valid for the slot they
 * would occupy, so a letter typed/pasted into a digit slot (or vice versa)
 * is dropped instead of rendered.
 */
const sanitizePan = (raw: string): string => {
  let out = '';
  for (const ch of raw.toUpperCase()) {
    if (out.length === 10) break;
    const expectsLetter = out.length < 5 || out.length === 9;
    if (expectsLetter ? /[A-Z]/.test(ch) : /[0-9]/.test(ch)) {
      out += ch;
    }
  }
  return out;
};

const buildInitialValues = (defaultAggregator?: string, allowStateSelection?: boolean): CreateConsentFormValues => ({
  accountAggregator: defaultAggregator ?? '',
  productIDList: [],
  ProductDivision: 'all',
  accountID: '',
  partyIdentifierType: PARTY_IDENTIFIER_OPTIONS[0].value,
  partyIdentifierValue: '',
  pan: '',
  dob: '',
  vua: defaultAggregator ?? '',
  email: '',
  firstName: '',
  lastName: '',
  pincode: '',
  mobileNumber: '',
  expiresOn: '',
  genderCode: '',
  addressline: '',
  request_type: 'V3',
  user_state: allowStateSelection ? '' : '',
});

const sanitizeVua = (value: string): string => {
  // Only trim whitespace, do NOT change case
  // The AA handle (e.g., @CAMSAA) must match the central registry exactly
  // This matches the Angular behavior which does not apply any case conversion
  return value.trim();
};

const validateVua = (value: string): boolean => {
  if (!value.includes('@')) {
    return false;
  }
  const [identifier, domain] = value.split('@');
  if (!identifier || !domain) {
    return false;
  }
  const regex = /^[a-zA-Z0-9_.-]+$/;
  return regex.test(identifier) && regex.test(domain);
};

const generateAccountId = () => {
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `ACC-${Date.now().toString().slice(-6)}-${random}`;
};

const deriveTemplateNames = (selectedCodes: string[], products: ProductRecord[]): string[] => {
  const lookup = new Map(products.map((product) => [product.product_code, product.product_name]));
  return selectedCodes
    .map((code) => lookup.get(code))
    .filter((label): label is string => Boolean(label));
};

export const CreateConsentDrawer: React.FC<CreateConsentDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  aggregators,
  products,
  productDivisions,
  states,
  multiConsentLimit,
  allowMultiConsent,
  allowAdditionalInfo,
  allowStateSelection,
  endUserListEnabled,
  defaultAggregator,
  onFetchEndUserDetails,
  onGenerateAccountId,
  onDownloadGuidelines,
  onViewTerms,
  successMessage,
  errorMessage,
  consentSuccessData,
  onResetSuccess,
  assistedJourneyFlags: propAssistedJourneyFlags,
}) => {
  const [formValues, setFormValues] = useState<CreateConsentFormValues>(
    buildInitialValues(defaultAggregator, allowStateSelection)
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);

  // Copy to clipboard hook with proper timer cleanup
  const { copied: copySuccess, copy: copyToClipboard } = useCopyToClipboard();

  // Get assisted journey flags - use product-level flags from props if provided,
  // otherwise fall back to global sessionStorage flags for backward compatibility
  const assistedJourneyFlags = useMemo(() => {
    if (propAssistedJourneyFlags) {
      return propAssistedJourneyFlags;
    }
    // Fall back to global flags (deprecated - for backward compatibility)
    return getAssistedJourneyFlags();
  }, [propAssistedJourneyFlags]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  // FP-6598 — portal "Send via SMS" state
  const [smsSending, setSmsSending] = useState(false);
  const [smsRemaining, setSmsRemaining] = useState<number | null>(null);
  const [smsToast, setSmsToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const smsLimitReached = smsRemaining !== null && smsRemaining <= 0;

  useEffect(() => {
    if (isOpen) {
      setFormValues(buildInitialValues(defaultAggregator, allowStateSelection));
      setErrors({});
      setPrefillError(null);
      setProductSearchTerm('');
      // FP-6598: reset per-consent SMS state so a new consent doesn't inherit the
      // previous consent's "Limit reached" / remaining-count / toast.
      setSmsSending(false);
      setSmsRemaining(null);
      setSmsToast(null);
    }
  }, [isOpen, defaultAggregator, allowStateSelection]);

  // FP-6598: also reset SMS state when a NEW consent success is shown in-place
  // (drawer stays open), so consent B never inherits consent A's remaining/limit state.
  useEffect(() => {
    setSmsSending(false);
    setSmsRemaining(null);
    setSmsToast(null);
  }, [consentSuccessData?.consentHandle]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filter by division
    if (formValues.ProductDivision !== 'all' && formValues.ProductDivision) {
      filtered = filtered.filter(
        (product) =>
          (product.product_division || '').toLowerCase() === formValues.ProductDivision.toLowerCase()
      );
    }

    // Filter by search term
    if (productSearchTerm.trim()) {
      const searchLower = productSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (product) =>
          product.product_code.toLowerCase().includes(searchLower) ||
          product.product_name.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [formValues.ProductDivision, products, productSearchTerm]);

  const selectedAggregator = useMemo(
    () => aggregators.find((item) => item.value === formValues.accountAggregator) ?? null,
    [aggregators, formValues.accountAggregator]
  );

  const templateNames = useMemo(
    () => deriveTemplateNames(formValues.productIDList, products),
    [formValues.productIDList, products]
  );

  const handleInputChange = useCallback(
    (field: keyof CreateConsentFormValues, value: string | string[]) => {
      setFormValues((prev) => {
        const next = { ...prev };
        const isArrayValue = Array.isArray(value);
        const normalizedValue = isArrayValue ? value : value ?? '';

        next[field] = normalizedValue as never;

        if (field === 'partyIdentifierValue' && selectedAggregator && typeof value === 'string') {
          const sanitized = value ? `${value}${selectedAggregator.value}` : selectedAggregator.value;
          next.vua = sanitizeVua(sanitized);
        }

        if (field === 'accountAggregator' && typeof value === 'string') {
          const aggregatorHandle = value;
          const currentIdentifier = prev.partyIdentifierValue || '';
          next.vua = sanitizeVua(
            currentIdentifier ? `${currentIdentifier}${aggregatorHandle}` : aggregatorHandle
          );
        }

        if (field === 'ProductDivision' && allowMultiConsent && typeof value === 'string') {
          const normalizedDivision = value.toLowerCase();
          const allowedProducts = products
            .filter((product) => {
              if (value === 'all') return true;
              return (product.product_division || '').toLowerCase() === normalizedDivision;
            })
            .map((product) => product.product_code);
          const filteredSelections = prev.productIDList.filter((code) => allowedProducts.includes(code));
          next.productIDList = filteredSelections;
        }

        return next;
      });

      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [selectedAggregator, products, allowMultiConsent]
  );

  // Handle multi-select product selection changes
  const handleProductSelectionChange = useCallback(
    (values: string[]) => {
      if (allowMultiConsent) {
        // Enforce multi-consent limit
        if (values.length > multiConsentLimit) {
          values = values.slice(0, multiConsentLimit);
        }
        handleInputChange('productIDList', values);
      } else {
        // Single selection mode - take only first value
        handleInputChange('productIDList', values.slice(0, 1));
      }
    },
    [allowMultiConsent, multiConsentLimit, handleInputChange]
  );

  // Handle single product selection from dropdown
  const handleSingleProductSelect = useCallback(
    (productCode: string) => {
      handleInputChange('productIDList', [productCode]);
    },
    [handleInputChange]
  );

  const validateForm = useCallback((): FormErrors => {
    const validationErrors: FormErrors = {};

    if (!formValues.accountAggregator) {
      validationErrors.accountAggregator = 'Please select an account aggregator';
    }

    if (!formValues.productIDList || formValues.productIDList.length === 0) {
      validationErrors.productIDList = 'Select at least one consent template';
    }

    if (!formValues.accountID) {
      validationErrors.accountID = 'Loan/Application ID is required';
    } else if (!/^([a-zA-Z0-9_.-]+)$/.test(formValues.accountID)) {
      validationErrors.accountID = 'Only alphanumeric, dot, underscore and hyphen allowed';
    }

    if (!formValues.partyIdentifierValue) {
      validationErrors.partyIdentifierValue = 'Customer identifier is required';
    } else if (!/^[6-9]{1}[0-9]{9}$/.test(formValues.partyIdentifierValue)) {
      validationErrors.partyIdentifierValue = 'Enter a valid 10-digit mobile number';
    }

    // PAN & DOB are optional additional identifiers; validate strictly only when provided.
    if (formValues.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formValues.pan)) {
      validationErrors.pan = 'Enter a valid PAN (e.g., ABCDE1234F)';
    }

    if (formValues.dob && !/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(formValues.dob)) {
      validationErrors.dob = 'Enter a valid date of birth';
    } else if (formValues.dob && formValues.dob > new Date().toISOString().slice(0, 10)) {
      validationErrors.dob = 'Date of birth cannot be in the future';
    }

    if (!formValues.vua) {
      validationErrors.vua = 'VUA is required';
    } else if (!validateVua(formValues.vua)) {
      validationErrors.vua = 'Enter a valid VUA (example: mobile@onemoney)';
    }

    if (formValues.email && !/^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(formValues.email)) {
      validationErrors.email = 'Enter a valid email address';
    }

    if (formValues.firstName && !/^([a-zA-Z_.-]+)$/.test(formValues.firstName)) {
      validationErrors.firstName = 'Only alphabets, dot, hyphen and underscore allowed';
    }

    if (formValues.lastName && !/^([a-zA-Z_.-]+)$/.test(formValues.lastName)) {
      validationErrors.lastName = 'Only alphabets, dot, hyphen and underscore allowed';
    }

    if (formValues.pincode && !/^([0-9]{6})$/.test(formValues.pincode)) {
      validationErrors.pincode = 'Enter a valid 6-digit PIN code';
    }

    if (allowStateSelection && formValues.user_state && Number.isNaN(Number(formValues.user_state))) {
      validationErrors.user_state = 'Select a valid state';
    }

    return validationErrors;
  }, [formValues, allowStateSelection]);

  const handlePrefill = useCallback(
    async (mobileNumber: string) => {
      if (!endUserListEnabled || !onFetchEndUserDetails) {
        return;
      }

      if (!/^[6-9]{1}[0-9]{9}$/.test(mobileNumber)) {
        return;
      }

      try {
        setPrefillLoading(true);
        setPrefillError(null);
        const data = await onFetchEndUserDetails(mobileNumber);
        if (data) {
          setFormValues((prev) => ({
            ...prev,
            firstName: (data.firstName as string) ?? prev.firstName,
            lastName: (data.lastName as string) ?? prev.lastName,
            email: (data.email as string) ?? prev.email,
            addressline: (data.addressline as string) ?? prev.addressline,
            pincode: (data.pincode as string) ?? prev.pincode,
            genderCode: (data.genderCode as string) ?? prev.genderCode,
            mobileNumber,
          }));
        }
      } catch (error) {
        console.error('[CreateConsentDrawer] Failed to prefill user data', error);
        setPrefillError('Unable to fetch end-user details. Please continue manually.');
      } finally {
        setPrefillLoading(false);
      }
    },
    [endUserListEnabled, onFetchEndUserDetails]
  );

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Destructure to exclude productIDList from spread - we'll add it conditionally
    const { productIDList, ...restFormValues } = formValues;

    const payload: ConsentCreatePayload = {
      ...restFormValues,
      // Send productIDList when multi-consent is allowed, otherwise send productID (matches Angular behavior)
      ...(allowMultiConsent
        ? { productIDList }
        : { productID: productIDList[0] }),
      accountID: formValues.accountID.trim(),
      partyIdentifierValue: formValues.partyIdentifierValue.trim(),
      vua: sanitizeVua(formValues.vua),
      pan: formValues.pan.trim(),
      dob: formValues.dob.trim(),
    };

    // Omit optional identifiers when not supplied.
    if (!payload.pan) {
      delete payload.pan;
    }
    if (!payload.dob) {
      delete payload.dob;
    }

    if (!allowStateSelection) {
      delete payload.user_state;
    } else if (payload.user_state === '') {
      delete payload.user_state;
    } else if (typeof payload.user_state === 'string') {
      payload.user_state = Number(payload.user_state);
    }

    await onSubmit(payload);
  }, [formValues, allowMultiConsent, allowStateSelection, onSubmit, validateForm]);

  const handleAutoGenerate = useCallback(() => {
    const generated = onGenerateAccountId ? onGenerateAccountId() : generateAccountId();
    handleInputChange('accountID', generated);
  }, [onGenerateAccountId, handleInputChange]);

  /**
   * Copy consent link to clipboard
   */
  const handleCopyLink = useCallback(async () => {
    if (!consentSuccessData?.consentLink) return;
    await copyToClipboard(consentSuccessData.consentLink);
  }, [consentSuccessData?.consentLink, copyToClipboard]);

  /**
   * Open WhatsApp with pre-filled message
   */
  const handleWhatsApp = useCallback(() => {
    if (!consentSuccessData?.consentLink || !consentSuccessData?.mobileNumber) return;
    const phone = toWhatsAppNumber(consentSuccessData.mobileNumber);
    const message = encodeURIComponent(
      `Please complete your consent journey using this link:\n${consentSuccessData.consentLink}`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank', 'noopener,noreferrer');
  }, [consentSuccessData?.consentLink, consentSuccessData?.mobileNumber]);

  /**
   * FP-6598 — send the consent link via SMS (portal manual send). Shows a toast with
   * the remaining attempts on every send; disables the button when the cap is reached.
   */
  const handleSendSms = useCallback(async () => {
    if (smsSending) return;
    if (!consentSuccessData?.consentHandle) {
      setSmsToast({ message: 'Cannot send SMS: consent handle is missing.', variant: 'error' });
      return;
    }
    setSmsSending(true);
    try {
      const body = await consentRequestAPI.sendAssistedJourneySms(consentSuccessData.consentHandle);
      const remaining = body?.data?.sendsRemaining;
      if (typeof remaining === 'number') setSmsRemaining(remaining);
      const remainingText =
        typeof remaining === 'number' ? ` ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.` : '';
      setSmsToast({ message: `SMS sent successfully.${remainingText}`, variant: 'success' });
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const message = err?.response?.data?.message || err?.message || '';
      if (status === 429 || /limit/i.test(message)) {
        setSmsRemaining(0);
        setSmsToast({ message: 'SMS send limit reached for this consent.', variant: 'error' });
      } else {
        setSmsToast({ message: message || 'Failed to send SMS. Please try again.', variant: 'error' });
      }
    } finally {
      setSmsSending(false);
    }
  }, [consentSuccessData?.consentHandle, smsSending]);

  /**
   * Open consent link in same tab
   * Validates URL before navigation to prevent open redirect vulnerabilities
   */
  const handleOpenLink = useCallback(() => {
    if (!consentSuccessData?.consentLink) return;
    try {
      const url = new URL(consentSuccessData.consentLink);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        console.warn('[CreateConsentDrawer] Invalid URL protocol:', url.protocol);
        return;
      }
    } catch {
      console.warn('[CreateConsentDrawer] Invalid consent link URL');
      return;
    }
    window.location.href = consentSuccessData.consentLink;
  }, [consentSuccessData?.consentLink]);

  /**
   * Handle closing the drawer - reset success state if needed
   */
  const handleClose = useCallback(() => {
    if (consentSuccessData && onResetSuccess) {
      onResetSuccess();
    }
    onClose();
  }, [consentSuccessData, onResetSuccess, onClose]);

  // Show success view when consent is created successfully
  const showSuccessView = Boolean(consentSuccessData?.consentLink);

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="720px"
        className="bg-white shadow-2xl border border-border-default"
      >
        <DrawerHeader
          title="Create Consent"
          subtitle={showSuccessView
            ? "Create a consent using an RBI-licensed account aggregator of your choice to fetch financial information"
            : "Select templates, provide customer details, and initiate a new consent journey."
          }
          onClose={handleClose}
        />
        <DrawerContent className="p-6 space-y-6 overflow-y-auto">
          {/* Success View - Consent Journey Created */}
          {showSuccessView && consentSuccessData ? (
            <div className="flex flex-col" style={{ minHeight: '100%' }}>
              {/* Main Content - Horizontal Layout */}
              <div className="flex gap-3 items-start flex-shrink-0">
                {/* Left Side - QR Code (240x240 with rounded corners as per Figma) */}
                <div
                  className="flex-shrink-0 border-2 border-border-light overflow-hidden"
                  style={{
                    width: '240px',
                    height: '240px',
                    borderRadius: '32px',
                    padding: '0',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <StyledQRCode
                    url={consentSuccessData.consentLink}
                    logoUrl="assets/images/finpro-f-logo.png"
                    size={240}
                    color="#1a3a4a"
                    bgColor="#ffffff"
                    logoSize={0.22}
                    dotsType="dots"
                    cornersSquareType="dot"
                    cornersDotType="dot"
                  />
                </div>

                {/* Right Side - Content */}
                <div className="flex-1 flex flex-col justify-between self-stretch">
                  {/* Success Header */}
                  <div className="mb-2">
                    <div className="flex items-center gap-1">
                      <h2
                        className="font-bold tracking-wide"
                        style={{
                          fontSize: '20px',
                          color: '#0f3340',
                          fontFamily: 'Helvetica Neue, sans-serif'
                        }}
                      >
                        Consent Journey Created!
                      </h2>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="10" fill="#2d8659"/>
                        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p
                      className="mt-2"
                      style={{
                        fontSize: '16px',
                        color: '#666d78',
                        fontFamily: 'Helvetica Neue, sans-serif'
                      }}
                    >
                      <span className="font-bold">👈🏻 </span>
                      Scan the QR code to open the journey in any device.
                    </p>
                  </div>

                  {/* You can also section */}
                  <div className="mt-auto">
                    <p
                      className="mb-1.5"
                      style={{
                        fontSize: '12px',
                        color: '#666d78',
                        fontFamily: 'Helvetica Neue, sans-serif'
                      }}
                    >
                      You can also:
                    </p>

                    {/* Container for link and buttons */}
                    <div
                      className="border border-border-light p-3 space-y-4"
                      style={{ borderRadius: '8px', backgroundColor: '#ffffff' }}
                    >
                      {/* Link display with copy button - Green background */}
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
                        style={{
                          backgroundColor: '#e6f4ec',
                          borderRadius: '4px',
                          height: '32px'
                        }}
                        title={copySuccess ? 'Copied!' : 'Click to copy link'}
                      >
                        <span
                          className="flex-1 text-left truncate"
                          style={{
                            fontSize: '14px',
                            color: '#2d8659',
                            fontFamily: 'Helvetica Neue, sans-serif',
                            fontWeight: 500
                          }}
                        >
                          {consentSuccessData.consentLink}
                        </span>
                        {copySuccess ? (
                          <Check className="w-3 h-3 flex-shrink-0" style={{ color: '#2d8659' }} />
                        ) : (
                          <Copy className="w-3 h-3 flex-shrink-0" style={{ color: '#2d8659' }} />
                        )}
                      </button>

                      {/* FP-6598 — SMS send feedback toast */}
                      {smsToast && (
                        <Toast
                          message={smsToast.message}
                          variant={smsToast.variant}
                          duration={4000}
                          onClose={() => setSmsToast(null)}
                        />
                      )}

                      {/* Action buttons - SMS & WhatsApp on left, Open Link on right */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                        {/* Left side - SMS and WhatsApp buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {/* SMS Button - Conditional (FP-6598) */}
                          {assistedJourneyFlags.smsEnabled && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleSendSms}
                              disabled={smsSending || smsLimitReached}
                            >
                              <MessageSquare className="w-4 h-4" />
                              {smsSending ? 'Sending…' : smsLimitReached ? 'Limit reached' : 'Send via SMS'}
                            </Button>
                          )}

                          {/* WhatsApp Button - Conditional */}
                          {assistedJourneyFlags.whatsappEnabled && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleWhatsApp}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              Send via Whatsapp
                            </Button>
                          )}
                        </div>

                        {/* Right side - Open Link button (primary - same as Create Consent) */}
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleOpenLink}
                        >
                          Open Link
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 17L17 7M17 7H7M17 7V17"/>
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom - New Consent Request Button - pushed to bottom */}
              <div className="mt-auto pt-6 border-t border-border-light flex-shrink-0">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    if (onResetSuccess) {
                      onResetSuccess();
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  NEW CONSENT REQUEST
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Form View */}
              {successMessage ? (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {successMessage}
                </div>
              ) : null}
              {errorMessage ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errorMessage}
                </div>
              ) : null}

              <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text-primary">Consent Information</h2>
              <div className="flex items-center gap-3">
                {templateNames.length > 0 ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {templateNames.map((name) => (
                      <span
                        key={name}
                        className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-primary-50 text-primary-700 border border-primary-100"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Account Aggregator<span className="text-red-500 ml-1">*</span>
                </label>
                <Dropdown>
                  <DropdownTrigger
                    className={`w-full justify-between ${
                      errors.accountAggregator ? 'border-red-300' : ''
                    }`}
                    style={{ borderColor: errors.accountAggregator ? undefined : DROPDOWN_BORDER_COLOR }}
                  >
                    {selectedAggregator?.label || 'Select account aggregator'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={1400} className="max-h-[200px] overflow-y-auto">
                    {aggregators.map((aggregator) => (
                      <DropdownItem
                        key={aggregator.value}
                        onSelect={() => handleInputChange('accountAggregator', aggregator.value)}
                      >
                        {aggregator.label}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
                {errors.accountAggregator ? (
                  <p className="text-xs text-red-500 mt-1">{errors.accountAggregator}</p>
                ) : null}
                {selectedAggregator?.logoUrl ? (
                  <div className="mt-3">
                    <img
                      src={selectedAggregator.logoUrl}
                      alt={selectedAggregator.label}
                      className="h-8 w-auto rounded"
                    />
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Product Division
                </label>
                <Dropdown>
                  <DropdownTrigger className="w-full justify-between" style={{ borderColor: DROPDOWN_BORDER_COLOR }}>
                    {formValues.ProductDivision === 'all'
                      ? 'All Divisions'
                      : formValues.ProductDivision || 'Select division'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={1400} className="max-h-[200px] overflow-y-auto">
                    <DropdownItem onSelect={() => handleInputChange('ProductDivision', 'all')}>
                      All Divisions
                    </DropdownItem>
                    {productDivisions.map((division) => (
                      <DropdownItem
                        key={division}
                        onSelect={() => handleInputChange('ProductDivision', division)}
                      >
                        {division}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-2">
                Consent Template Code<span className="text-red-500 ml-1">*</span>
              </label>
              {allowMultiConsent ? (
                // Multi-select dropdown with checkboxes
                <Dropdown>
                  <DropdownTrigger
                    className={`w-full justify-between ${
                      errors.productIDList ? 'border-red-300' : ''
                    }`}
                    style={{ borderColor: errors.productIDList ? undefined : DROPDOWN_BORDER_COLOR }}
                  >
                    {formValues.productIDList.length > 0
                      ? `${formValues.productIDList.length} template${formValues.productIDList.length > 1 ? 's' : ''} selected`
                      : 'Select consent templates'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={1400} className="max-h-[280px] overflow-y-auto p-0">
                    {/* Search input */}
                    <div className="px-3 py-2 border-b border-border-default sticky top-0 bg-background-secondary">
                      <input
                        type="text"
                        placeholder="Search consent template code..."
                        value={productSearchTerm}
                        onChange={(e) => {
                          e.stopPropagation();
                          setProductSearchTerm(e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full text-sm bg-transparent outline-none placeholder:text-text-tertiary"
                      />
                    </div>
                    {/* Options list with checkboxes */}
                    <div className="p-1">
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => {
                          const isSelected = formValues.productIDList.includes(product.product_code);
                          const isDisabled = !isSelected && formValues.productIDList.length >= multiConsentLimit;
                          return (
                            <div
                              key={product.product_code}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isDisabled) return;
                                if (isSelected) {
                                  handleProductSelectionChange(
                                    formValues.productIDList.filter((code) => code !== product.product_code)
                                  );
                                } else {
                                  handleProductSelectionChange([...formValues.productIDList, product.product_code]);
                                }
                              }}
                              className={`flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer hover:bg-primary-50 transition-colors ${
                                isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                              } ${isSelected ? 'bg-primary-50' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                readOnly
                                className="w-4 h-4 text-primary-600 border-border-default rounded focus:ring-primary-500 pointer-events-none"
                              />
                              <span className={`text-sm ${isSelected ? 'font-medium text-primary-700' : 'text-text-primary'}`}>
                                {product.product_code} ({product.product_name})
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-sm text-text-tertiary text-center">
                          No consent templates available
                        </div>
                      )}
                    </div>
                  </DropdownContent>
                </Dropdown>
              ) : (
                // Single-select dropdown using Equal DS
                <Dropdown>
                  <DropdownTrigger
                    className={`w-full justify-between ${
                      errors.productIDList ? 'border-red-300' : ''
                    }`}
                    style={{ borderColor: errors.productIDList ? undefined : DROPDOWN_BORDER_COLOR }}
                  >
                    {formValues.productIDList.length > 0
                      ? (() => {
                          const product = filteredProducts.find(
                            (p) => p.product_code === formValues.productIDList[0]
                          );
                          return product
                            ? `${product.product_code} (${product.product_name})`
                            : formValues.productIDList[0];
                        })()
                      : 'Select consent template'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={1400} className="max-h-[280px] overflow-y-auto">
                    {filteredProducts.map((product) => (
                      <DropdownItem
                        key={product.product_code}
                        onSelect={() => handleSingleProductSelect(product.product_code)}
                      >
                        {product.product_code} ({product.product_name})
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              )}
              <div className="flex items-center justify-between mt-2 text-xs text-text-tertiary">
                <span>
                  {allowMultiConsent
                    ? `You may select up to ${multiConsentLimit} templates`
                    : 'Select a single template'}
                </span>
                <span>{formValues.productIDList.length} selected</span>
              </div>
              {errors.productIDList ? (
                <p className="text-xs text-red-500 mt-1">{errors.productIDList}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <FormField
                  label="Loan/Application ID"
                  name="accountID"
                  type="text"
                  value={formValues.accountID}
                  onChange={(value) => handleInputChange('accountID', value)}
                  error={errors.accountID}
                  placeholder="Unique customer reference"
                  variant="outlined"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={handleAutoGenerate}
                >
                  Auto Generate Identifier
                </Button>
              </div>

              {allowStateSelection ? (
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    State / Union Territory
                  </label>
                  <Dropdown>
                    <DropdownTrigger
                      className={`w-full justify-between ${
                        errors.user_state ? 'border-red-300' : ''
                      }`}
                      style={{ borderColor: errors.user_state ? undefined : DROPDOWN_BORDER_COLOR }}
                    >
                      {formValues.user_state
                        ? states.find((s) => String(s.state_id) === formValues.user_state)?.state_name || 'Select state'
                        : 'Select state'}
                    </DropdownTrigger>
                    <DropdownContent zIndex={1400} className="max-h-[200px] overflow-y-auto">
                      {states.map((state) => (
                        <DropdownItem
                          key={state.state_id}
                          onSelect={() => handleInputChange('user_state', String(state.state_id))}
                        >
                          {state.state_name}
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </Dropdown>
                  {errors.user_state ? (
                    <p className="text-xs text-red-500 mt-1">{errors.user_state}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Customer Identification</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Identifier Type<span className="text-red-500 ml-1">*</span>
                </label>
                <Dropdown>
                  <DropdownTrigger className="w-full justify-between" style={{ borderColor: DROPDOWN_BORDER_COLOR }}>
                    {PARTY_IDENTIFIER_OPTIONS.find((opt) => opt.value === formValues.partyIdentifierType)?.label ||
                      'Select type'}
                  </DropdownTrigger>
                  <DropdownContent zIndex={1400}>
                    {PARTY_IDENTIFIER_OPTIONS.map((option) => (
                      <DropdownItem
                        key={option.value}
                        onSelect={() => handleInputChange('partyIdentifierType', option.value)}
                      >
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              </div>

              <FormField
                label="Identifier Value"
                name="partyIdentifierValue"
                type="tel"
                value={formValues.partyIdentifierValue}
                onChange={(value) => {
                  handleInputChange('partyIdentifierValue', value);
                  if (endUserListEnabled && value.length === 10) {
                    void handlePrefill(value);
                  }
                }}
                error={errors.partyIdentifierValue}
                placeholder="10-digit mobile number"
                maxLength={10}
                variant="outlined"
              />

              <FormField
                label="PAN"
                name="pan"
                type="text"
                value={formValues.pan}
                onChange={(value) => {
                  // Drop characters that don't fit their PAN slot (AAAAA9999A) so they never render.
                  const pan = sanitizePan(value);
                  handleInputChange('pan', pan);
                  // Still needed: a partial PAN (e.g. "ABCDE12") passes the filter but is incomplete.
                  setErrors((prev) => ({
                    ...prev,
                    pan:
                      pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)
                        ? 'Enter a valid PAN (e.g., ABCDE1234F)'
                        : undefined,
                  }));
                }}
                error={errors.pan}
                placeholder="ABCDE1234F"
                maxLength={10}
                variant="outlined"
              />

              <FormField
                label="Date of Birth"
                name="dob"
                type="date"
                value={formValues.dob}
                onChange={(value) => handleInputChange('dob', value)}
                error={errors.dob}
                max={new Date().toISOString().slice(0, 10)}
                variant="outlined"
              />
            </div>

            <FormField
              label="Virtual User Address (VUA)"
              name="vua"
              type="text"
              value={formValues.vua}
              onChange={(value) => handleInputChange('vua', sanitizeVua(value))}
              error={errors.vua}
              placeholder="e.g., 9876543210@onemoney"
              variant="outlined"
            />

            {prefillLoading ? (
              <div className="text-xs text-text-secondary">
                Fetching customer details from end-user list...
              </div>
            ) : null}
            {prefillError ? (
              <div className="text-xs text-red-500">{prefillError}</div>
            ) : null}
          </section>

          {allowAdditionalInfo ? (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-primary">Additional Information</h2>
                <span className="text-xs text-text-tertiary">
                  Optional, but helps personalise the consent journey.
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={formValues.email}
                  onChange={(value) => handleInputChange('email', value)}
                  error={errors.email}
                  placeholder="customer@example.com"
                  variant="outlined"
                />
                <FormField
                  label="First Name"
                  name="firstName"
                  type="text"
                  value={formValues.firstName}
                  onChange={(value) => handleInputChange('firstName', value)}
                  error={errors.firstName}
                  placeholder="First name"
                  variant="outlined"
                />
                <FormField
                  label="Last Name"
                  name="lastName"
                  type="text"
                  value={formValues.lastName}
                  onChange={(value) => handleInputChange('lastName', value)}
                  error={errors.lastName}
                  placeholder="Last name"
                  variant="outlined"
                />
                <FormField
                  label="PIN Code"
                  name="pincode"
                  type="tel"
                  value={formValues.pincode}
                  onChange={(value) => handleInputChange('pincode', value)}
                  error={errors.pincode}
                  placeholder="6-digit PIN"
                  maxLength={6}
                  variant="outlined"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Address"
                  name="addressline"
                  type="text"
                  value={formValues.addressline}
                  onChange={(value) => handleInputChange('addressline', value)}
                  placeholder="Address line"
                  variant="outlined"
                />
                <FormField
                  label="Gender Code"
                  name="genderCode"
                  type="text"
                  value={formValues.genderCode}
                  onChange={(value) => handleInputChange('genderCode', value)}
                  placeholder="M / F / O"
                  variant="outlined"
                />
              </div>
            </section>
          ) : null}

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Guidelines & Support</h2>
                <p className="text-sm text-text-secondary">
                  Ensure the customer understands how their data will be utilised before initiating the journey.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {onDownloadGuidelines ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onDownloadGuidelines}
                  >
                    Download Guidelines
                  </Button>
                ) : null}
                {onViewTerms ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onViewTerms}
                  >
                    View Terms
                  </Button>
                ) : null}
              </div>
            </div>
          </section>
            </>
          )}
        </DrawerContent>

        {/* Footer - only show for form view, success view has its own button */}
        {!showSuccessView && (
          <div className="border-t border-border-default bg-white p-4 flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating…' : 'Create Consent'}
            </Button>
          </div>
        )}
      </Drawer>
    </>
  );
};
